<template>
  <div class="reporting__click">
    <h3><span class="title"><i class="icon icon--logo-mobile"></i> {{ $t('reporting.swipe.title') }}</span></h3>

    <ui-button v-if="hasData"
      class="reporting__export"
      :class="currentLang"
      :size="'small'"
      @click="onExcelExport"
      primary3>
      <i class="icon icon-excel-size16"></i> {{ $t('reporting.export') }}
    </ui-button>

    <!-- no data -->
    <div v-if="!hasData" class="no-data">
      <p v-html="$t('reporting.swipe.noSwipeData')"></p>
    </div>
    <div v-else>
      <div class="legends"
           :class="{ closed: !showLegend }"
           @click="showLegend = !showLegend">
        <h5 class="title"
            v-if="!showLegend">
          <i class="icon icon--question-orange"></i> {{ $t('common.legend') }}
        </h5>
        <div class="legends-wrap"
             v-if="showLegend">
          <h6>
            {{ $t('common.unit.swipeVertical') }}
            <span>(%)</span>
          </h6>
          <div class="legend">
            <div class="item">
              <i class="icon up weight-5"></i>
              <span>100 ~ 80</span>
              <i class="icon down weight-5"></i>
            </div>
            <div class="item">
              <i class="icon up weight-4"></i>
              <span>79 ~ 60</span>
              <i class="icon down weight-4"></i>
            </div>
            <div class="item">
              <i class="icon up weight-3"></i>
              <span>59 ~ 40</span>
              <i class="icon down weight-3"></i>
            </div>
            <div class="item">
              <i class="icon up weight-2"></i>
              <span>39 ~ 20</span>
              <i class="icon down weight-2"></i>
            </div>
            <div class="item">
              <i class="icon up weight-1"></i>
              <span>19 ~ 1</span>
              <i class="icon down weight-1"></i>
            </div>
          </div>
        </div>
        <div class="legends-wrap second"
             v-if="showLegend">
          <h6>
            {{ $t('common.unit.swipeHorizontal') }}
            <span>(%)</span>
          </h6>
          <div class="legend weight">
            <div class="item">
              <i class="icon left weight-5"></i>
              <span>100 ~ 80</span>
              <i class="icon right weight-5"></i>
            </div>
            <div class="item">
              <i class="icon left weight-4"></i>
              <span>79 ~ 60</span>
              <i class="icon right weight-4"></i>
            </div>
            <div class="item">
              <i class="icon left weight-3"></i>
              <span>59 ~ 40</span>
              <i class="icon right weight-3"></i>
            </div>
            <div class="item">
              <i class="icon left weight-2"></i>
              <span>39 ~ 20</span>
              <i class="icon right weight-2"></i>
            </div>
            <div class="item">
              <i class="icon left weight-1"></i>
              <span>19 ~ 1</span>
              <i class="icon right weight-1"></i>
            </div>
          </div>
        </div>
        <div class="close-legends">
          <i class="icon icon--tab-btn"></i>
        </div>
      </div>

      <ul class="reporting__click__type">
        <li v-for="item in reportModes" :key="item.name"
          :class="{on: item.name === reportMode}"
          style="cursor: pointer;"
          @click="onReportModeClick(item.name)">{{ item.text }}</li>
      </ul>

      <!-- ranking summary -->
      <div class="reporting__click__rank-summary content-box">
        <div>
          <div class="reporting__click__rank-summary-title">
            <span class="title" v-html="reportMode === 'upDown' ? $t('reporting.swipe.upDown.summary.title') : $t('reporting.swipe.leftRight.summary.title')"></span>
            <ui-icon-tooltip iconSize="normal"><span v-html="summaryDescription"></span></ui-icon-tooltip>
          </div>
          <p class="notification" v-html="reportMode === 'upDown' ? $t('reporting.swipe.upDown.summary.notice') : $t('reporting.swipe.leftRight.summary.notice')"></p>
          <div class="reporting__click__rank-summary-chart">
            <app-chart chartType="pie" v-if="pieChartOptions"
              :chartOptions="pieChartOptions"
              :width="134"
              :height="134"/>
            <div class="total">
              <span>{{ $t('reporting.swipe.total') }}</span> {{ targetItems.leftTotal + targetItems.rightTotal | thousand }}
            </div>
          </div>
          <ul class="top_list">
            <li>
              <span class="item rank">
                <span class="marker" :style="{ backgroundColor: pieChartOptions.color[1] }"></span>
                {{ leftLabel }} :
              </span>
              <span class="item label">{{ targetItems.leftTotal | thousand }}</span>
              <span class="item percentage">{{ getPercentage(targetItems.leftTotal, targetItems.leftTotal + targetItems.rightTotal) | fixed(1) }}%</span>
            </li>
            <li>
              <span class="item rank">
                <span class="marker" :style="{ backgroundColor: pieChartOptions.color[0] }"></span>
                {{ rightLabel }} :
              </span>
              <span class="item label">{{ targetItems.rightTotal | thousand }}</span>
              <span class="item percentage">{{ getPercentage(targetItems.rightTotal, targetItems.leftTotal + targetItems.rightTotal) | fixed(1) }}%</span>
            </li>
          </ul>
        </div>
      </div>

      <slot name="footer"/>
    </div>
  </div>
</template>

<script>
import { thousand, getPercentage } from '@/libs/utils'
import { getTooltipHtml } from '@/libs/chart-helper'
import Excel from '@/libs/excel-export'
import AppChart from '@/components/chart'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import UiButton from '@/components/ui/UiButton'
import { mapState } from 'vuex'

export default {
  name: 'ReportSwipe',
  components: {
    AppChart,
    UiIconTooltip,
    UiButton
  },
  props: [
    'isLoading',
    'reportName',
    'reportData',
    'infoData',
    'referer',
    'metric',
    'activeSelector',
    'heatmapType'
  ],
  data () {
    return {
      reportModes: [
        {
          name: 'upDown',
          text: this.$t('reporting.swipe.upDown.subTitle')
        },
        {
          name: 'leftRight',
          text: this.$t('reporting.swipe.leftRight.subTitle')
        }
      ],
      reportMode: 'upDown',
      showLegend: true
    }
  },
  computed: {
    ...mapState([
      'applyData'
    ]),
    leftLabel () {
      return this.$t(['common.unit.swipeUp', 'common.unit.swipeLeft'][this.heatmapType])
    },
    rightLabel () {
      return this.$t(['common.unit.swipeDown', 'common.unit.swipeRight'][this.heatmapType])
    },
    isAMP () {
      return this.applyData.bAMP
    },
    currentLang () {
      return this.$i18n.locale
    },
    isDesktopDevice () {
      return this.reportData.device === 'desktop'
    },
    keyColumn () {
      switch (this.reportMode) {
        case 'upDown':
          return 0
        case 'leftRight':
          return 1
        default:
          throw new Error(`invalid reportMode: ${this.reportMode}`)
      }
    },
    summaryDescription () {
      if (this.reportMode === 'upDown') {
        return this.$t('reporting.swipe.upDown.summary.desc')
      } else {
        return this.$t('reporting.swipe.leftRight.summary.desc')
      }
    },
    hasData () {
      return !!this.targetItems
    },
    targetItems () {
      let items = null

      if (this.reportData && this.reportData.data) {
        switch (this.reportMode) {
          case 'upDown':
            items = this.reportData.data[0]
            break
          case 'leftRight':
            items = this.reportData.data[1]
        }
      }

      if (!items) {
        return null
      }

      return items
    },
    pieChartOptions () {
      if (!this.targetItems) {
        return null
      }

      let seriesNameKey

      if (this.reportMode === 'click') {
        seriesNameKey = 'reporting.click.clickCount.subTitle'
      } else {
        seriesNameKey = 'reporting.click.pvCount.subTitle'
      }

      return {
        xAxis: {
          right: 0
        },
        yAxis: {
          splitNumber: 2
        },
        width: 134,
        height: 134,
        series: [
          {
            name: this.$t(seriesNameKey),
            type: 'pie',
            data: [
              {
                name: 'right',
                value: this.targetItems.rightTotal
              },
              {
                name: 'left',
                value: this.targetItems.leftTotal
              }
            ],
            hoverAnimation: false,
            label: {
              show: false
            },
            tooltip: {
              show: false
            },
            silent: true,
            radius: ['35%', '100%']
          }
        ],
        color: [['#f8d136', '#8a6aff'], ['#c7d937', '#e75ebf']][this.heatmapType],
        tooltip: {
          formatter (params) {
            const param = params[0]
            const title = param.data[0]
            const content = [
              [
                `${param.marker}${param.seriesName} :`,
                `${thousand(param.data[1])}`
              ]
            ]
            return getTooltipHtml(content, title)
          }
        }
      }
    }
  },
  watch: {
    heatmapType (value) {
      if (value === 1) {
        this.onReportModeClick('leftRight')
      } else {
        this.onReportModeClick('upDown')
      }
    }
  },
  methods: {
    getPercentage (value, total) {
      return getPercentage(value, total)
    },
    setHeatmapType (value) {
      this.$emit('setHeatmapType', value)
    },
    onReportModeClick (value) {
      this.reportMode = value
      if (value === 'leftRight') {
        this.setHeatmapType(1)
      } else {
        this.setHeatmapType(0)
      }
      this.$emit('onActiveSelectorChange', null)
    },
    highlightCoords (item) {
      let selector = item.coords.selector

      if (selector === this.activeSelector) {
        selector = null
      }

      this.$emit('onActiveSelectorChange', selector)
    },
    onExcelExport () {
      let vue = this
      let fileName = this.applyData.pageName + '_' + this.reportName
      let excel = new Excel()
      vue.$emit('onDownloading', true)
      excel.setReportInfo(this.applyData, this.infoData, this.reportData, this.referer, this.metric)
        .then(
          excel.setReportData(this.reportName, this.reportData, this.reportData || [], this.reportData.image.url, { heatmapType: ['vert', 'horiz'][this.heatmapType] })
            .then(result => {
              excel.download(fileName)
              vue.$emit('onDownloading', false)
            })
        )
    }
  },
  destroyed () {
    this.$emit('onActiveSelectorChange', null)
  }
}
</script>

<style lang="scss" scoped>
  .reporting__click {
    position: relative;

    h3 {
      font-size: 22px;
      font-weight: 500;
      color: #444444;

      i {
        vertical-align: middle;
        margin-right: 3px;
      }
    }

    .reporting__export {
      position: absolute;
      top: -5px;
      right: 0;
      font-size: 12px;

      .icon {
        margin-right: 3px;
        vertical-align: middle;
      }

      &.ja {
        padding-top: 1px;
      }
      &.en {
        .icon {
          transform: translateY(-1px);
        }
      }

      &.browser-ie {
        padding-top: 4px;

        .icon {
          transform: translateY(-1px);
        }

        &.en {
          padding-top: 2px;
        }
      }

      &.browser-safari {
        padding-top: 1px;

        .icon {
          transform: translateY(-1px);
        }

        &.en {
          padding-top: 2px;
        }
      }

      &.platform-window {
        &.en {
          padding-top: 1px;
        }
      }
    }

    .legends {
      position: relative;
      margin-top: 20px;
      margin-bottom: 41px;
      border-radius: 14px;
      box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
      background-color: #1b1b1d;
      cursor: pointer;
      padding-top: 1px;

      h5 {
        line-height: 16px;
        color: #ff8400;
        font-size: 13px;
        padding: 22px 25px;

        i {
          vertical-align: middle;
          margin-right: 2px;
          transform: translateY(-1px);
        }
      }

      h6 {
        display: inline-block;
        vertical-align: top;
        width: 60px;
        line-height: 16px;
        color: #ff8400;
        font-size: 13px;
        margin: 26px 0 10px 25px;

        span {
          display: block;
          color: #aaaaaa;
          margin-top: 13px;
        }
      }

      .legends-wrap {
        &::after {
          display: block;
          content: "";
          border-top: 1px solid #333333;
          margin: 19px 25px -8px 25px;
        }
      }

      .legend {
        display: inline-block;
        vertical-align: top;
        width: calc(100% - 110px);
        margin: 20px 25px 0 0;

        .item {
          display: inline-block;
          width: 21%;
          text-align: center;
          color: #aaaaaa;
          font-size: 11px;

          &:first-child {
            width: 23%;
          }

          &:last-child {
            width: 14%;
          }

          .icon {
            width: 100%;
            height: 50px;
            background-position: center;
            background-size: auto;
            background-repeat: no-repeat;

            &.up {
              &.weight-1 {
                background-image: url(../../assets/img/grid-cell-swipe-up-1.svg);
              }

              &.weight-2 {
                background-image: url(../../assets/img/grid-cell-swipe-up-2.svg);
              }

              &.weight-3 {
                background-image: url(../../assets/img/grid-cell-swipe-up-3.svg);
              }

              &.weight-4 {
                background-image: url(../../assets/img/grid-cell-swipe-up-4.svg);
              }

              &.weight-5 {
                background-image: url(../../assets/img/grid-cell-swipe-up-5.svg);
              }
            }

            &.down {
              &.weight-1 {
                background-image: url(../../assets/img/grid-cell-swipe-down-1.svg);
              }

              &.weight-2 {
                background-image: url(../../assets/img/grid-cell-swipe-down-2.svg);
              }

              &.weight-3 {
                background-image: url(../../assets/img/grid-cell-swipe-down-3.svg);
              }

              &.weight-4 {
                background-image: url(../../assets/img/grid-cell-swipe-down-4.svg);
              }

              &.weight-5 {
                background-image: url(../../assets/img/grid-cell-swipe-down-5.svg);
              }
            }

            &.left {
              &.weight-1 {
                background-image: url(../../assets/img/grid-cell-swipe-left-1.svg);
              }

              &.weight-2 {
                background-image: url(../../assets/img/grid-cell-swipe-left-2.svg);
              }

              &.weight-3 {
                background-image: url(../../assets/img/grid-cell-swipe-left-3.svg);
              }

              &.weight-4 {
                background-image: url(../../assets/img/grid-cell-swipe-left-4.svg);
              }

              &.weight-5 {
                background-image: url(../../assets/img/grid-cell-swipe-left-5.svg);
              }
            }

            &.right {
              &.weight-1 {
                background-image: url(../../assets/img/grid-cell-swipe-right-1.svg);
              }

              &.weight-2 {
                background-image: url(../../assets/img/grid-cell-swipe-right-2.svg);
              }

              &.weight-3 {
                background-image: url(../../assets/img/grid-cell-swipe-right-3.svg);
              }

              &.weight-4 {
                background-image: url(../../assets/img/grid-cell-swipe-right-4.svg);
              }

              &.weight-5 {
                background-image: url(../../assets/img/grid-cell-swipe-right-5.svg);
              }
            }
          }
        }
      }

      .close-legends {
        position: absolute;
        z-index: 2;
        right: 27px;
        bottom: -19px;
        width: 30px;
        height: 19px;
        border-top: none;
        border-bottom-right-radius: 6px;
        border-bottom-left-radius: 6px;
        transform: translateY(-50%);
        background: #1b1b1d;
        text-align: center;

        &::after,
        &::before{
          content: "";
          width: 5px;
          height: 5px;
          border: 3px solid #1b1b1d;
          position: absolute;
          top: 6px;
          border-bottom: none;
        }

        &::after{
          border-left: none;
          border-radius: 0 8px 0 0;
          left: -5px;
        }

        &::before{
          border-right: none;
          border-radius: 8px 0 0 0;
          right: -5px;
        }
      }

      &.closed {
        .close-legends {
          .icon {
            transform: rotate(180deg);
          }
        }
      }

      @media screen and (min-width: 1790px) {
        .legends-wrap {
          vertical-align: top;
          display: inline-block;
          width: 50%;
          padding-bottom: 20px;

          &::after {
            display: none;
          }
        }

        h6 {
          padding-bottom: 9px;
        }
      }
    }

    .reporting__click__type {
      margin-top: 30px;

      li {
        display: inline-block;
        font-size: 15px;
        font-weight: 500;
        color: #999999;
        padding-bottom: 8px;

        &:hover {
          color: #444;
        }

        &:nth-last-of-type(1) {
          margin-left: 36px;
        }

        &.on {
          color: #2f2f2f;
          border-bottom: 3px solid $brandColor;
        }
      }
    }

    .reporting__click__rank-summary {
      margin-top: 20px;

      .reporting__click__rank-summary-title {
        .title {
          font-size: 15px;
          font-weight: 500;
          color: #2f2f2f;
        }

        .tooltip {
          margin-left: 8px;
          margin-top: -2px;
        }

        .more-info {
          float: right;
          cursor: pointer;
          i {
            margin-right: 4px;
          }
          span {
            font-weight: bold;
            color: #444444;
            line-height: 13px;
          }
        }
      }

      .notification {
        font-size: 12px;
        color: $brandColor;
        margin-top: 20px;
      }

      .reporting__click__rank-summary-chart {
        position: relative;
        margin-top: 30px;
        margin-left: 15px;
        margin-bottom: 32px;

        .total {
          position: absolute;
          top: calc(50% - 5px);
          left: 184px;
          transform: translateY(-50%);
          color: #444444;
          font-size: 25px;

          span {
            display: inline-block;
            vertical-align: middle;
            font-size: 14px;

          }
        }
      }
    }

    .top_list {
      font-size: 12px;
      color: #444;
      margin-bottom: -10px;

      li {
        position: relative;
        margin: 10px 0 10px 0;
        height: 18px;
        line-height: 18px;
        overflow: hidden;
        background: url(../../assets/img/bg_dotted_line.gif) repeat-x 0 8px;

        &> .item {
          float: left;
          height: 100%;
          background-color: #fff;
        }

        .marker {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 4px;
          margin-top: 2px;
          margin-right: 3px;
        }

        .rank {
          padding-right: 1ch;
          font-size: 13px;
        }

        .label {
          display: inline-block;
          max-width: calc(100% - 110px);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          padding-right: 10px;
        }

        .percentage {
          float: right;
          padding-left: 10px;
        }
      }
    }

    .btn__back-to-top {
      cursor: pointer;
      width: 100px;
      margin: 40px auto 0;
      text-align: center;

      p {
        margin-top: 8px;
      }
    }
  }

</style>
